import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';

import { useAuth } from 'context/auth';
import { EventContext } from 'context/event';
import { useNotification } from 'context/notification';
import InfoTooltip from 'components/popups/InfoTooltip';

import type { ReportDateRangeValue } from './api/fetchReportData';
import type { ReportView, ReportViewVisibility } from './api/reportViewTypes';
import {
    useCreateReportView,
    useDeleteReportView,
    useForkReportView,
    useShareReportView,
    useUpdateReportView,
} from './hooks/useReportViews';
import { useReportData } from './hooks/useReportData';
import { useEventTickets } from './hooks/useEventTickets';
import type { ReportDefinition } from './mock/reports';
import type { ReportPageLimit } from './reportQueryParams';
import { getReportTableConfig } from './reportTableConfigs';
import {
    filterRowsByTicketIds,
    mergeTicketOptions,
    reportHasTicketNameColumn,
    uniqueTicketNamesFromRows,
    areAllTicketsSelected,
    resolveTicketNamesToIds,
} from './ticketFilter';
import {
    applyReportViewConfigToParams,
    areReportViewConfigsEqual,
    buildReportViewConfig,
    getValidColumnIdsForReport,
} from './reportViewConfig';
import { CustomizeReportColumnsModal } from './CustomizeReportColumnsModal';
import { CloneReportViewModal } from './CloneReportViewModal';
import { DeleteReportViewModal } from './DeleteReportViewModal';
import { exportReport } from './exportReportData';
import { ReportCategoryBadge } from './ReportCategoryBadge';
import { ReportDataTable } from './ReportDataTable';
import { ReportToolbar } from './ReportToolbar';
import { SaveReportViewModal } from './SaveReportViewModal';
import { ShareReportViewModal } from './ShareReportViewModal';
import type { ReportQueryParams } from './reportQueryParams';

type ReportViewerProps = {
    report: ReportDefinition;
    eventName: string;
    dateRange: ReportDateRangeValue;
    onDateRangeChange: (value: ReportDateRangeValue) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    page: number;
    limit: ReportPageLimit;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: ReportPageLimit) => void;
    ticketIds?: number[];
    onTicketIdsChange: (ticketIds: number[] | undefined) => void;
    selectedView: ReportView | null;
    myViews?: ReportView[];
    onApplyViewParams: (patch: Partial<ReportQueryParams>) => void;
};

export function ReportViewer({
    report,
    eventName,
    dateRange,
    onDateRangeChange,
    searchValue,
    onSearchChange,
    sorting,
    onSortingChange,
    page,
    limit,
    onPageChange,
    onLimitChange,
    ticketIds,
    onTicketIdsChange,
    selectedView,
    myViews = [],
    onApplyViewParams,
}: ReportViewerProps) {
    const event = useContext(EventContext);
    const { auth } = useAuth();
    const { notify } = useNotification();
    const tableConfig = getReportTableConfig(report.id);
    const showTicketFilter = reportHasTicketNameColumn(tableConfig?.columns);
    const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(() =>
        getValidColumnIdsForReport(report.id, tableConfig?.defaultSelectedColumnIds),
    );
    const [isFieldsModalOpen, setIsFieldsModalOpen] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

    const createView = useCreateReportView(event?.event_id);
    const updateView = useUpdateReportView(event?.event_id);
    const forkView = useForkReportView(event?.event_id);
    const shareView = useShareReportView(event?.event_id);
    const deleteView = useDeleteReportView(event?.event_id);

    // Ownership drives Save/Share/Delete vs Clone UI. Backend also enforces owner-only mutate/delete.
    const isOwner = Boolean(
        selectedView && auth?.userId && String(selectedView.ownerUserId) === String(auth.userId),
    );

    const {
        data: rows = [],
        isPending: isReportPending,
        isFetching: isReportFetching,
        isError: isReportError,
    } = useReportData({
        reportId: report.id,
        eventId: event?.event_id,
        dateRange,
        timezone: event?.time_zone || 'America/Toronto',
        enabled: Boolean(tableConfig && event?.event_id),
    });

    const { data: catalogTickets = [], isSuccess: isTicketCatalogReady } = useEventTickets(
        event?.event_id,
        showTicketFilter,
    );

    const isLoading =
        isReportPending ||
        isReportFetching ||
        (showTicketFilter && ticketIds !== undefined && !isTicketCatalogReady);

    useEffect(() => {
        if (selectedView && selectedView.baseReportId === report.id) {
            setSelectedColumnIds(
                getValidColumnIdsForReport(report.id, selectedView.config.columnIds),
            );
            return;
        }

        setSelectedColumnIds(
            getValidColumnIdsForReport(report.id, tableConfig?.defaultSelectedColumnIds),
        );
    }, [report.id, selectedView, tableConfig?.defaultSelectedColumnIds]);

    const columnDefinitions = useMemo(() => tableConfig?.columns ?? [], [tableConfig]);

    const ticketOptions = useMemo(
        () => mergeTicketOptions(catalogTickets, uniqueTicketNamesFromRows(rows), ticketIds),
        [catalogTickets, rows, ticketIds],
    );

    const visibleRows = useMemo(
        () =>
            showTicketFilter && isTicketCatalogReady
                ? filterRowsByTicketIds(rows, ticketIds, ticketOptions)
                : rows,
        [isTicketCatalogReady, rows, showTicketFilter, ticketIds, ticketOptions],
    );

    const ticketIdsForConfig =
        !showTicketFilter || areAllTicketsSelected(ticketOptions, ticketIds)
            ? undefined
            : ticketIds;

    const migratedLegacyViewIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!selectedView) {
            migratedLegacyViewIdRef.current = null;
            return;
        }

        if (!showTicketFilter || !isTicketCatalogReady) {
            return;
        }

        if (migratedLegacyViewIdRef.current === selectedView.eventReportViewId) {
            return;
        }

        if (selectedView.config.ticketIds !== undefined || ticketIds !== undefined) {
            migratedLegacyViewIdRef.current = selectedView.eventReportViewId;
            return;
        }

        const legacyNames = selectedView.config.ticketNames;
        if (legacyNames === undefined) {
            migratedLegacyViewIdRef.current = selectedView.eventReportViewId;
            return;
        }

        const resolved = resolveTicketNamesToIds(legacyNames, ticketOptions);
        migratedLegacyViewIdRef.current = selectedView.eventReportViewId;
        onTicketIdsChange(areAllTicketsSelected(ticketOptions, resolved) ? undefined : resolved);
    }, [
        isTicketCatalogReady,
        onTicketIdsChange,
        selectedView,
        showTicketFilter,
        ticketIds,
        ticketOptions,
    ]);

    const draftConfig = useMemo(
        () =>
            buildReportViewConfig({
                columnIds: selectedColumnIds,
                dateRange,
                sort: sorting,
                search: searchValue,
                limit,
                ticketIds: ticketIdsForConfig,
            }),
        [selectedColumnIds, dateRange, sorting, searchValue, limit, ticketIdsForConfig],
    );

    // Disable "Save changes" until the owned view actually differs from what is persisted.
    const hasUnsavedOwnedViewChanges = Boolean(
        selectedView && isOwner && !areReportViewConfigsEqual(draftConfig, selectedView.config),
    );

    const handleColumnsApply = async (columnIds: string[]) => {
        setSelectedColumnIds(columnIds);

        // Shared views are read-only for the original — cloning is required to keep edits.
        if (selectedView && !isOwner) {
            setIsCloneModalOpen(true);
        }
    };

    const handleSaveOwnedView = async () => {
        if (!selectedView || !isOwner || !hasUnsavedOwnedViewChanges) {
            return;
        }

        try {
            await updateView.mutateAsync({
                viewId: selectedView.eventReportViewId,
                input: {
                    name: selectedView.name,
                    description: selectedView.description,
                    config: draftConfig,
                    visibility: selectedView.visibility,
                },
            });
            notify.success('View changes saved');
        } catch {
            notify.error('Unable to save view changes');
        }
    };

    const handleCreateView = async (name: string) => {
        try {
            const created = await createView.mutateAsync({
                baseReportId: report.id,
                name,
                config: draftConfig,
                visibility: 'private',
            });
            setIsSaveModalOpen(false);
            // Land the user on the newly created view (cache is upserted in the mutation hook).
            onApplyViewParams(
                applyReportViewConfigToParams(
                    created.baseReportId,
                    created.eventReportViewId,
                    created.config,
                ),
            );
            notify.success('View saved');
        } catch {
            notify.error('Unable to save view');
        }
    };

    const handleSaveClick = () => {
        if (selectedView && isOwner) {
            void handleSaveOwnedView();
            return;
        }

        // No selected view → create a new private view via the name dialog.
        setIsSaveModalOpen(true);
    };

    /** Fork a shared view into the current user's My views, then navigate to that copy. */
    const handleCloneView = async (name: string) => {
        if (!selectedView || isOwner) {
            return;
        }

        try {
            const forked = await forkView.mutateAsync({
                viewId: selectedView.eventReportViewId,
                input: {
                    name,
                    config: draftConfig,
                },
            });
            setIsCloneModalOpen(false);
            onApplyViewParams(
                applyReportViewConfigToParams(
                    forked.baseReportId,
                    forked.eventReportViewId,
                    forked.config,
                ),
            );
            notify.success('View cloned');
        } catch {
            notify.error('Unable to clone view');
        }
    };

    const handleShare = async (visibility: ReportViewVisibility) => {
        if (!selectedView || !isOwner) {
            return;
        }

        return shareView.mutateAsync({
            viewId: selectedView.eventReportViewId,
            visibility,
        });
    };

    const handleDeleteView = async () => {
        // Owner-only (UI + API). Prefer next My view, else previous, else base report.
        if (!selectedView || !isOwner) {
            return;
        }

        const deletedViewId = selectedView.eventReportViewId;
        const baseReportId = selectedView.baseReportId;
        const currentIndex = myViews.findIndex((view) => view.eventReportViewId === deletedViewId);
        const nextView =
            currentIndex === -1
                ? null
                : (myViews[currentIndex + 1] ?? myViews[currentIndex - 1] ?? null);

        try {
            await deleteView.mutateAsync(deletedViewId);
            setIsDeleteModalOpen(false);

            if (nextView) {
                onApplyViewParams(
                    applyReportViewConfigToParams(
                        nextView.baseReportId,
                        nextView.eventReportViewId,
                        nextView.config,
                    ),
                );
            } else {
                onApplyViewParams({
                    report: baseReportId,
                    view: undefined,
                });
            }

            notify.success('View deleted');
        } catch {
            notify.error('Unable to delete view');
        }
    };

    const saveLabel = selectedView && isOwner ? 'Save changes' : 'Save view';
    // Shared / link views: show Clone instead of Save so the user can make an editable copy.
    const canCloneView = Boolean(selectedView && !isOwner);

    return (
        <div className="reports-viewer flex h-full min-h-0 flex-col overflow-hidden p-4">
            <div className="shrink-0">
                <div className="mb-4 flex items-center gap-2">
                    <h2 className="m-0 text-2xl font-semibold text-foreground">
                        {selectedView?.name || report.title}
                    </h2>
                    <ReportCategoryBadge category={report.category} />
                    {selectedView?.description || report.description ? (
                        <InfoTooltip message={selectedView?.description || report.description} />
                    ) : null}
                </div>
            </div>

            <div className="shrink-0">
                <ReportToolbar
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    dateRange={dateRange}
                    onDateRangeChange={onDateRangeChange}
                    eventName={eventName}
                    showTicketFilter={showTicketFilter}
                    ticketOptions={ticketOptions}
                    selectedTicketIds={ticketIds}
                    onTicketIdsChange={onTicketIdsChange}
                    onCustomizeColumns={() => setIsFieldsModalOpen(true)}
                    exportDisabled={isLoading || !tableConfig}
                    onExport={(format) =>
                        exportReport(format, {
                            rows: visibleRows,
                            columnDefinitions,
                            selectedColumnIds,
                            searchValue,
                            sorting,
                            reportTitle: selectedView?.name || report.title,
                            reportCategory: report.category,
                        })
                    }
                    onSaveView={canCloneView ? undefined : handleSaveClick}
                    onShareView={() => setIsShareModalOpen(true)}
                    onDeleteView={() => setIsDeleteModalOpen(true)}
                    onCloneView={() => setIsCloneModalOpen(true)}
                    saveLabel={saveLabel}
                    canShareView={Boolean(selectedView && isOwner)}
                    canDeleteView={Boolean(selectedView && isOwner)}
                    canCloneView={canCloneView}
                    saveDisabled={
                        createView.isPending ||
                        updateView.isPending ||
                        forkView.isPending ||
                        deleteView.isPending ||
                        (Boolean(selectedView && isOwner) && !hasUnsavedOwnedViewChanges)
                    }
                    deleteDisabled={deleteView.isPending}
                    cloneDisabled={forkView.isPending}
                />
            </div>

            <div className="mt-6 flex min-h-0 flex-1 flex-col">
                {report.hasTable && tableConfig ? (
                    <>
                        {isReportError ? (
                            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                Unable to load report data.
                            </div>
                        ) : null}
                        <ReportDataTable
                            data={visibleRows}
                            columnDefinitions={columnDefinitions}
                            isLoading={isLoading}
                            globalFilter={searchValue}
                            selectedColumnIds={selectedColumnIds}
                            sorting={sorting}
                            onSortingChange={onSortingChange}
                            page={page}
                            limit={limit}
                            onPageChange={onPageChange}
                            onLimitChange={onLimitChange}
                        />
                    </>
                ) : (
                    <div className="flex h-48 items-center justify-center rounded-md border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
                        Coming soon
                    </div>
                )}
            </div>

            {tableConfig ? (
                <CustomizeReportColumnsModal
                    open={isFieldsModalOpen}
                    onOpenChange={setIsFieldsModalOpen}
                    columnDefinitions={columnDefinitions}
                    categories={tableConfig.categories}
                    defaultColumnIds={tableConfig.defaultSelectedColumnIds}
                    selectedColumnIds={selectedColumnIds}
                    onApply={(columnIds) => {
                        void handleColumnsApply(columnIds);
                    }}
                />
            ) : null}

            <SaveReportViewModal
                open={isSaveModalOpen}
                onOpenChange={setIsSaveModalOpen}
                defaultName={`${report.title} view`}
                isSaving={createView.isPending}
                onSave={(name) => {
                    void handleCreateView(name);
                }}
            />

            <ShareReportViewModal
                open={isShareModalOpen}
                onOpenChange={setIsShareModalOpen}
                view={selectedView}
                isSaving={shareView.isPending}
                onShare={handleShare}
            />

            <DeleteReportViewModal
                open={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                view={selectedView}
                isDeleting={deleteView.isPending}
                onConfirm={() => {
                    void handleDeleteView();
                }}
            />

            <CloneReportViewModal
                open={isCloneModalOpen}
                onOpenChange={setIsCloneModalOpen}
                view={selectedView}
                isCloning={forkView.isPending}
                onClone={(name) => {
                    void handleCloneView(name);
                }}
            />
        </div>
    );
}
